/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Organization Settings Hooks
 * ============================================================================
 *
 * React Query hooks for managing organization-specific settings.
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { organizationSettingsService } from '@/services/superadmin/organization-settings.service';
import {
  OrganizationSettingsDto,
  UpdateOrganizationSettingsRequest,
} from '@/types/superadmin.types';

// Query keys
export const orgSettingsKeys = {
  all: ['organization-settings'] as const,
  lists: () => [...orgSettingsKeys.all, 'list'] as const,
  byOrg: (orgId: string) => [...orgSettingsKeys.all, 'org', orgId] as const,
  available: () => [...orgSettingsKeys.all, 'available'] as const,
};

/**
 * Hook to fetch organization settings
 */
export function useOrganizationSettings(orgId: string | undefined) {
  return useQuery<OrganizationSettingsDto>({
    queryKey: orgSettingsKeys.byOrg(orgId || ''),
    queryFn: () => organizationSettingsService.getSettings(orgId!),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to update organization settings
 */
export function useUpdateOrganizationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orgId,
      settings,
    }: {
      orgId: string;
      settings: Record<string, string | null>;
    }) => organizationSettingsService.updateSettings(orgId, { settings }),
    onSuccess: (data, variables) => {
      // Update the cache with new data
      queryClient.setQueryData(orgSettingsKeys.byOrg(variables.orgId), data);
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: orgSettingsKeys.lists() });
    },
  });
}

/**
 * Hook to reset organization settings to defaults
 */
export function useResetOrganizationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orgId: string) => organizationSettingsService.resetSettings(orgId),
    onSuccess: (_, orgId) => {
      // Invalidate the settings query to refetch
      queryClient.invalidateQueries({ queryKey: orgSettingsKeys.byOrg(orgId) });
    },
  });
}

/**
 * Hook to get available setting definitions
 */
export function useAvailableSettings() {
  return useQuery({
    queryKey: orgSettingsKeys.available(),
    queryFn: () => organizationSettingsService.getAvailableSettings(),
    staleTime: 30 * 60 * 1000, // 30 minutes (rarely changes)
  });
}
